/**
 * Urban Equestrian Design System
 * Premium/Luxury Design Tokens
 *
 * Philosophy: Restraint, clarity, craftsmanship
 */

export const designTokens = {
  // SPACING SCALE (4px base unit)
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '16px',
    lg: '24px',
    xl: '32px',
    '2xl': '48px',
    '3xl': '64px',
    '4xl': '96px',
    '5xl': '128px',
  },

  // BORDER RADIUS
  radius: {
    none: '0px',
    sm: '6px',
    md: '10px',
    lg: '16px',
    full: '9999px',
  },

  // SHADOWS (2-level max for premium feel)
  shadows: {
    subtle: '0 8px 24px rgba(20, 56, 125, 0.08)',
    elevated: '0 22px 55px rgba(20, 56, 125, 0.14)',
    elevatedDark: '0 22px 55px rgba(0, 0, 0, 0.28)',
    none: 'none',
  },

  // TYPOGRAPHY SCALE
  typography: {
    // Font Families
    fontFamily: {
      sans: '"Helvetica Neue", Helvetica, Arial, sans-serif',
      serif: '"Helvetica Neue", Helvetica, Arial, sans-serif',
    },

    // Font Sizes
    fontSize: {
      xs: '12px',
      sm: '13px',
      base: '15px',
      md: '16px',
      lg: '18px',
      xl: '20px',
      '2xl': '24px',
      '3xl': '30px',
      '4xl': '36px',
      '5xl': '48px',
    },

    // Font Weights
    fontWeight: {
      light: 300,
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },

    // Line Heights
    lineHeight: {
      tight: 1.2,
      snug: 1.4,
      normal: 1.6,
      relaxed: 1.8,
      loose: 2.0,
    },

    // Letter Spacing
    letterSpacing: {
      tight: '-0.5px',
      normal: '0px',
      wide: '0.3px',
      wider: '1px',
    },
  },

  // COLORS (Urban Equestrian blue palette)
  colors: {
    // Light Mode
    light: {
      primary: '#2759B8',
      primaryDark: '#0B1B3B',
      accent: '#6D96E8',

      background: {
        default: '#F3F6FB',
        subtle: '#F8FAFD',
        elevated: '#FFFFFF',
      },

      text: {
        primary: '#0B1834',
        secondary: '#536078',
        tertiary: '#77839A',
        disabled: '#AAB3C2',
      },

      border: {
        default: '#D7E0EF',
        subtle: '#E9EFF8',
        emphasis: '#6D96E8',
      },
    },

    // Dark Mode
    dark: {
      primary: '#8DB2FF',
      primaryDark: '#638EE7',
      accent: '#AEC9FF',

      background: {
        default: '#07142B',
        subtle: '#0B2045',
        elevated: '#102C5B',
      },

      text: {
        primary: '#F6F8FC',
        secondary: '#D7E2F5',
        tertiary: '#AEBBD2',
        disabled: '#71819E',
      },

      border: {
        default: '#2B4775',
        subtle: '#1A3766',
        emphasis: '#8DB2FF',
      },
    },
  },

  // TRANSITIONS (Smooth, premium easing)
  transitions: {
    duration: {
      fast: '150ms',
      normal: '250ms',
      slow: '350ms',
    },
    easing: {
      default: 'cubic-bezier(0.4, 0, 0.2, 1)',
      in: 'cubic-bezier(0.4, 0, 1, 1)',
      out: 'cubic-bezier(0, 0, 0.2, 1)',
      inOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
    },
  },

  // BREAKPOINTS (Mobile-first)
  breakpoints: {
    xs: '0px',
    sm: '600px',
    md: '900px',
    lg: '1200px',
    xl: '1536px',
  },

  // Z-INDEX SCALE
  zIndex: {
    dropdown: 1000,
    sticky: 1020,
    fixed: 1030,
    modalBackdrop: 1040,
    modal: 1050,
    popover: 1060,
    tooltip: 1070,
  },
} as const;

// Type exports for TypeScript
export type DesignTokens = typeof designTokens;
export type Spacing = keyof typeof designTokens.spacing;
export type Radius = keyof typeof designTokens.radius;
export type Shadow = keyof typeof designTokens.shadows;
